import StaticAction from '../store/schema/StaticAction';
import ReadStaticAction from '../store/schema/ReadStaticAction';
import isMessageOptionsEnabled from './isMessageOptionsEnabled';
import { isEncryptionEnabled } from 'owa-encryption-common';
import { isFeatureEnabled } from 'owa-feature-flags';
import {
    ActionKey,
    getOptionsForFeature,
    OwsOptionsFeatureType,
    ReadActionKey,
    SurfaceActionsOptions,
} from 'owa-outlook-service-options';
import getUserConfiguration from 'owa-session-store/lib/actions/getUserConfiguration';
import { getIsDarkTheme } from 'owa-fabric-theme';
import isConsumer from 'owa-session-store/lib/utils/isConsumer';
import Message from 'owa-service/lib/contract/Message';
import { isBrowserChrome, isBrowserFirefox, isBrowserEdge } from 'owa-user-agent/lib/userAgent';

/**
 * Return all the actions that should be shown on an item in Reading Pane
 *
 * IMPORTANT: When adding or removing an action ensure that a corresponding lines are added/removed in the following files:
 * getIconNameFromKey.ts
 * getDisplayNameFromKey.ts
 */
export function getReadStaticActions(): ReadStaticAction[] {
    let actions: ReadStaticAction[] = [
        { key: 'Reply', hideInGroup: true, hideInSxS: false },
        { key: 'ReplyAll', hideInGroup: false, hideInSxS: false },
        { key: 'Forward', hideInGroup: false, hideInSxS: false },
    ];

    actions.push(
        { key: '-', hideInGroup: false, hideInSxS: false },
        { key: 'ReplyByMeeting', hideInGroup: true, hideInSxS: true },
        { key: '-', hideInGroup: false, hideInSxS: false },
        { key: 'Delete', hideInGroup: false, hideInSxS: true },
        { key: '-', hideInGroup: false, hideInSxS: false },
        { key: 'MarkReadUnread', hideInGroup: false, hideInSxS: true },
        { key: 'FlagUnflag', hideInGroup: true, hideInSxS: true },
        { key: '-', hideInGroup: false, hideInSxS: false }
    );

    if (isFeatureEnabled('fwk-skypeBusinessV2')) {
        actions.push(
            { key: 'ReplyByIM', hideInGroup: true, hideInSxS: false },
            { key: 'ReplyAllByIM', hideInGroup: true, hideInSxS: false },
            { key: '-', hideInGroup: true, hideInSxS: false }
        );
    }

    if (!getUserConfiguration().SessionSettings.IsShadowMailbox) {
        actions.push(
            { key: 'AddToSafeSenders', hideInGroup: true, hideInSxS: true },
            { key: 'MarkJunkNotJunk', hideInGroup: true, hideInSxS: true },
            { key: 'MarkAsPhishing', hideInGroup: true, hideInSxS: true },
            { key: 'Block', hideInGroup: true, hideInSxS: true }
        );

        if (isFeatureEnabled('tri-reportAbuse')) {
            actions.push({ key: 'ReportAbuse', hideInGroup: !isConsumer(), hideInSxS: true });
        }
        actions.push({ key: '-', hideInGroup: false, hideInSxS: false });
    }

    if (!isConsumer()) {
        actions.push(
            { key: 'AssignPolicy', hideInGroup: false, hideInSxS: true },
            { key: '-', hideInGroup: false, hideInSxS: false }
        );
    }

    if (!getUserConfiguration().SessionSettings.IsShadowMailbox) {
        actions.push({ key: 'CreateRule', hideInGroup: true, hideInSxS: true });
    }

    actions.push({ key: 'Print', hideInGroup: false, hideInSxS: false });

    if (
        isFeatureEnabled('rp-inlineTranslation') &&
        isFeatureEnabled('rp-inlineTranslationManual')
    ) {
        actions.push({ key: 'Translate', hideInGroup: true, hideInSxS: true });
    }

    if (getIsDarkTheme()) {
        actions.push({ key: 'ToggleDarkMode', hideInGroup: false, hideInSxS: false });
    }

    if (isFeatureEnabled('rp-immersiveReader')) {
        actions.push({ key: 'ShowInImmersiveReader', hideInGroup: false, hideInSxS: true });
    }

    actions.push({ key: 'ViewMessageSource', hideInGroup: false, hideInSxS: true });
    actions.push({ key: 'Popout', hideInGroup: false, hideInSxS: true });

    if (!isConsumer()) {
        actions.push({ key: 'LikeUnlike', hideInGroup: false, hideInSxS: true });
    }

    if (isFeatureEnabled('rp-amp') && isConsumer()) {
        actions.push({ key: 'ToggleAmp', hideInGroup: false, hideInSxS: true });
    }

    return actions;
}

/**
 * IMPORTANT: When adding or removing an action ensure that a corresponding lines are added/removed in the following files:
 * getIconNameFromKey.ts
 * getDisplayNameFromKey.ts
 */
export function getComposeStaticActions(): StaticAction[] {
    let actions: StaticAction[] = [
        { key: 'AddAttachment', hideInGroup: false },
        { key: 'AddInlineImage', hideInGroup: false },
    ];

    // If adding the expression picker button, it must be placed after
    // button to add inline image.
    actions.push({ key: 'AddEmoji', hideInGroup: false });

    // Dictation
    if (
        (isBrowserEdge() || isBrowserFirefox() || isBrowserChrome()) &&
        isFeatureEnabled('cmp-dictation')
    ) {
        actions.push({ key: 'ToggleDictation', hideInGroup: false });
    }

    // Add the rest of the always-provided actions.
    actions = actions.concat([
        { key: 'ToggleRibbon', hideInGroup: false },
        { key: 'SaveDraft', hideInGroup: false },
        { key: 'InsertSignature', hideInGroup: false },
        { key: 'ShowFrom', hideInGroup: false },
        { key: 'SetImportance', hideInGroup: false },
        { key: 'SwitchBodyType', hideInGroup: false },
        { key: 'AccChecker', hideInGroup: false },
    ]);

    if (isFeatureEnabled('cmp-clp') && !isConsumer()) {
        actions.push({ key: 'SensitivityMenu', hideInGroup: false });
    }

    if (isEncryptionEnabled()) {
        actions.push({ key: 'ProtectMessage', hideInGroup: false });
    }

    // TODO #33638 Move ShowMessageOptions to owa-mail.
    if (isMessageOptionsEnabled()) {
        actions.push({ key: 'ShowMessageOptions', hideInGroup: false });
    }

    return actions;
}

export function shouldAddActionToEnd(action: ActionKey): boolean {
    // Reply, ReplyAll, and Forward are the first 3 actions in the set of static actions in the reading pane.
    // While all other actions should render from right to left based on their order in the settings,
    // the desire is for these three to render left to right, so they should be inserted at the end of the collection.
    return action == 'Reply' || action == 'ReplyAll' || action == 'Forward';
}

export function isReadingPaneSurfaceActionPinnedByUser(actionKey: ReadActionKey) {
    const surfaceActionsOptions = getOptionsForFeature<SurfaceActionsOptions>(
        OwsOptionsFeatureType.SurfaceActions
    );

    return surfaceActionsOptions.readSurfaceActions.indexOf(actionKey) !== -1;
}

export function shouldActionBeOnReadingPaneSurface(
    actionKey: ReadActionKey,
    message: Message
): boolean {
    return (
        isReadingPaneSurfaceActionPinnedByUser(actionKey) ||
        // if there are likes & likers on a message, pin it
        // even if the CSA has not been set by the user
        !!(isFeatureEnabled('rp-csaPinnedLikes') && actionKey === 'LikeUnlike' && message.LikeCount)
    );
}
